import * as React from 'react';
import { cn } from '../../lib/utils';

/**
 * Standardized Typography System
 * Consistent text, labels, and placeholders across admin and user flows
 * Supports light and dark themes
 */

// Text Component - Base text with consistent styling
export const Text = React.forwardRef(function Text(
  { className, size = 'base', weight = 'normal', color = 'default', as: Component = 'p', ...props },
  ref
) {
  const sizeClasses = {
    xs: 'text-xs', // 12px
    sm: 'text-sm', // 14px
    base: 'text-base', // 16px
    lg: 'text-lg', // 18px
    xl: 'text-xl', // 20px
    '2xl': 'text-2xl', // 24px
    '3xl': 'text-3xl', // 30px
  };

  const weightClasses = {
    light: 'font-light', // 300
    normal: 'font-normal', // 400
    medium: 'font-medium', // 500
    semibold: 'font-semibold', // 600
    bold: 'font-bold', // 700
    bolder: 'font-extrabold', // 800
  };

  const colorClasses = {
    default: 'text-gray-900 dark:text-gray-100',
    muted: 'text-gray-600 dark:text-gray-400',
    secondary: 'text-gray-500 dark:text-gray-500',
    primary: 'text-indigo-600 dark:text-indigo-400',
    success: 'text-green-600 dark:text-green-400',
    error: 'text-red-600 dark:text-red-400',
    warning: 'text-yellow-600 dark:text-yellow-400',
  };

  return (
    <Component
      ref={ref}
      className={cn(
        sizeClasses[size],
        weightClasses[weight],
        colorClasses[color],
        'font-sans', // Consistent font family
        className
      )}
      {...props}
    />
  );
});

Text.displayName = 'Text';

// Label Component - Enhanced with standardized styling
export const Label = React.forwardRef(function Label(
  { className, size = 'sm', weight = 'medium', required = false, ...props },
  ref
) {
  const sizeClasses = {
    xs: 'text-xs',
    sm: 'text-sm',
    base: 'text-base',
  };

  const weightClasses = {
    light: 'font-light',
    normal: 'font-normal',
    medium: 'font-medium',
    semibold: 'font-semibold',
    bold: 'font-bold',
  };

  return (
    <label
      ref={ref}
      className={cn(
        sizeClasses[size],
        weightClasses[weight],
        'block text-gray-700 dark:text-gray-300 mb-1.5 font-sans',
        required && "after:content-['*'] after:ml-0.5 after:text-red-500",
        className
      )}
      {...props}
    />
  );
});

Label.displayName = 'Label';

// Placeholder styles - Applied via className
export const placeholderStyles = 'placeholder:text-gray-400 dark:placeholder:text-gray-500';

// Heading Component
export const Heading = React.forwardRef(function Heading(
  { className, level = 1, weight = 'bold', color = 'default', ...props },
  ref
) {
  const Component = `h${level}`;
  const sizeClasses = {
    1: 'text-3xl', // 30px
    2: 'text-2xl', // 24px
    3: 'text-xl', // 20px
    4: 'text-lg', // 18px
    5: 'text-base', // 16px
    6: 'text-sm', // 14px
  };

  const weightClasses = {
    light: 'font-light',
    normal: 'font-normal',
    medium: 'font-medium',
    semibold: 'font-semibold',
    bold: 'font-bold',
    bolder: 'font-extrabold',
  };

  const colorClasses = {
    default: 'text-gray-900 dark:text-gray-100',
    muted: 'text-gray-600 dark:text-gray-400',
    primary: 'text-indigo-600 dark:text-indigo-400',
  };

  return (
    <Component
      ref={ref}
      className={cn(
        sizeClasses[level],
        weightClasses[weight],
        colorClasses[color],
        'font-sans',
        className
      )}
      {...props}
    />
  );
});

Heading.displayName = 'Heading';

export default { Text, Label, Heading, placeholderStyles };
